// Ask AI on the Worklist and History: the grid's view in the lines reports' terms, and back — the
// one adapter between the grid's state and the terms the chat widget's tools use
// (`frontend/src/features/routing/axai.ts`):
//
// - the grid's filters, tab, date range and sort, said as the report's URL terms (`f_*`, `sort`) —
//   what the AI is told is on screen;
// - a view the AI applies (`apply_view`), merged into the grid's: only what it names changes, and
//   what the grid cannot show is named under the toolbar instead of being dropped silently;
// - the view as every count's default scope (`scopeOf`), with the words an answer says it in;
// - the page's default view, which "Clear filters" goes back to, and the filters shown as chips.
//
// It was the platform AskAiBar's adapter (P10); the platform AI is off in this app, and the same
// terms now come from the widget's tool call.

import { filterFromParam, filterOperatorParam, filterToParam, filterValueParam } from "@axny/platform-contracts";
import { A2000_VALUES } from "./a2000";
import type { LineView } from "./assemble";
import type { AskQuery } from "./ask-query";
import { formatDay, localToday } from "./days";
import { DEPOSCO_SHIPPED_VALUES, LINE_SOURCES } from "./deposco";
import type { LineSource, SourceFilter } from "./deposco";
import { DEFAULT_KINDS } from "./excel";
import {
  DEFAULT_WAREHOUSES,
  HISTORY_DATE_FIELDS,
  HISTORY_PRESETS,
  HISTORY_TABS,
  STATE_TABS,
  WAREHOUSE_GROUPS,
  WORKLIST_DATE_FIELDS,
  WORKLIST_PRESETS,
  WORKLIST_TABS,
  defaultRange,
  inTab,
  pageRows,
  presetRange,
} from "./filters";
import { divisionFilterName } from "./divisions";
import type { ColumnValue, DateFieldId, DateRange, Mode, Row, TabId, WarehouseGroup } from "./filters";
import { REVIEW_LABELS } from "./assemble";
import { WINDOW_STATE_LABELS } from "./window";

/** What of the page Ask AI reads and sets. */
export interface GridView {
  /** A2000, Deposco, or both ("all"). */
  source: SourceFilter;
  warehouses: WarehouseGroup[];
  customers: string[];
  statuses: string[];
  kinds: string[];
  duplicates: boolean;
  range: DateRange;
  /** Header "Show values" filters, by grid column id. */
  columns: Record<string, string[]>;
  tab: TabId;
  /** As TanStack Table's `SortingState`. */
  sorting: { id: string; desc: boolean }[];
  search: string;
}

/** The report's URL terms: one entry per parameter that is set. */
export type Search = Record<string, string | string[]>;

/** Grid date field → the report's date column, per page. */
const DATE_COLUMNS: Record<Mode, Partial<Record<DateFieldId, string>>> = {
  worklist: {
    ptDate: "received",
    start: "first_ship",
    cancel: "cancel",
    routeBy: "route_by",
    readyDate: "ready_date",
    schedShip: "sched_ship",
    appointmentDate: "appointment_date",
  },
  history: {
    ptDate: "received",
    start: "first_ship",
    cancel: "cancel",
    routedOn: "routed_on",
    finishedAt: "finished_on",
  },
};

/** Grid column id ↔ report column, for the header filters whose values are the same text. */
const HEADER_COLUMNS: Record<string, string> = {
  division: "division",
  workNeeded: "work_needed",
  inventoryNotes: "inventory_notes",
  lead: "lead",
  peStatus: "pe_status",
  peAssignedTo: "pe_assigned_to",
  review: "review",
  mixed: "mixed",
  a2000: "shipped_in_a2000",
  dshipped: "shipped_in_deposco",
  deposcoStatus: "deposco_status",
  stateTab: "state_tab",
  origin: "origin",
};

/** Grid column id ↔ report column, for sorting. */
const SORT_COLUMNS: Record<string, string> = {
  customer: "customer",
  division: "division",
  ptDate: "received",
  start: "first_ship",
  cancel: "cancel",
  readyDate: "ready_date",
  qty: "qty",
  window: "days_left",
  routeBy: "route_by",
  opens: "window_opens",
  routingStatus: "routing_status",
  schedShip: "sched_ship",
  appointmentDate: "appointment_date",
  routedOn: "routed_on",
  workNeeded: "work_needed",
  inventoryNotes: "inventory_notes",
  lead: "lead",
  peShipDate: "pe_ship_date",
  peStatus: "pe_status",
  peEstimate: "pe_estimate_minutes",
  pallets: "pe_pallets",
  finishedAt: "finished_on",
  origin: "origin",
  source: "source",
};

/** Text filters the AI may set that mean "find this line": the grid's search box. */
const SEARCH_COLUMNS = ["pick_tickets", "so_pt", "po", "wms", "routing_ref", "line_id", "dlix", "co", "group", "dc"];

/** Report columns that pick a Worklist tab: one of them in a query or a view replaces the tab. */
const TAB_COLUMNS = [
  "state_tab",
  "window_state",
  "review",
  "mixed",
  "two_values",
  "shipped_in_a2000",
  "shipped_in_deposco",
  "in_wms",
  "finished",
];
/** The Shipped in A2000 tab: some or all of a line's pick tickets shipped (`a2000.ts`). */
const A2000_TAB: string[] = A2000_VALUES.filter((v) => v !== "No");
/** The Shipped in Deposco tab, likewise. */
const DEPOSCO_TAB: string[] = DEPOSCO_SHIPPED_VALUES.filter((v) => v !== "No");

/** Grid columns whose header filter also picks a tab: a term naming a tab replaces them. */
const TAB_HEADERS = ["review", "mixed", "a2000", "dshipped", "stateTab"];

const STATE_TAB_LABEL: Partial<Record<TabId, string>> = Object.fromEntries(STATE_TABS.map((t) => [t.id, t.label]));
/** To review: New, Updated and Split (No WMS has its own tab). */
const NEEDS_REVIEW = [REVIEW_LABELS.new, REVIEW_LABELS.updated, REVIEW_LABELS.split];

/** Window states that make up a tab, as `inTab` groups them. */
const W = WINDOW_STATE_LABELS;
const WINDOW_TABS: { tab: TabId; states: string[] }[] = [
  { tab: "overdue", states: [W.overdue] },
  { tab: "now", states: [W.today, W.tomorrow] },
  { tab: "open", states: [W.due, W.open, W.requested] },
  { tab: "notyet", states: [W.notyet] },
  { tab: "routed", states: [W.routed] },
  { tab: "hold", states: [W.hold] },
  { tab: "norule", states: [W.norule] },
];

/**
 * The parts of a view a question or `apply_view` may leave out ("in all warehouses", "any date"),
 * or clear; `all` is every one.
 */
export const VIEW_PARTS = [
  "source",
  "warehouse",
  "date",
  "customer",
  "routing_status",
  "search",
  "tab",
  "columns",
  "kind",
  "duplicates",
] as const;
export type ViewPart = (typeof VIEW_PARTS)[number];

const list = (v: Search[string] | undefined): string[] => (v === undefined ? [] : Array.isArray(v) ? v : [v]);
const one = (v: Search[string] | undefined) => list(v)[0];
const swap = <K extends string, V extends string>(m: Record<K, V>) =>
  Object.fromEntries(Object.entries(m).map(([k, v]) => [v, k])) as Record<V, K>;
const partsOf = (asked: readonly string[]) =>
  new Set<ViewPart>(asked.includes("all") ? VIEW_PARTS : VIEW_PARTS.filter((p) => asked.includes(p)));

/** The page as it opens: PE + HT, its default date range, the first tab, no other filter. */
export function defaultView(mode: Mode, today: string): GridView {
  return {
    source: "all",
    warehouses: [...DEFAULT_WAREHOUSES],
    customers: [],
    statuses: [],
    kinds: mode === "history" ? [...DEFAULT_KINDS] : [],
    duplicates: false,
    range: defaultRange(mode, today),
    columns: {},
    tab: "all",
    sorting: [],
    search: "",
  };
}

/** Whether a view filters differently from the page as it opens ("Clear filters"); sorting aside. */
export function differsFromDefault(mode: Mode, view: GridView, today: string): boolean {
  const d = defaultView(mode, today);
  const same = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x) => b.includes(x));
  return (
    view.source !== d.source ||
    !same(view.warehouses, d.warehouses) ||
    view.customers.length > 0 ||
    view.statuses.length > 0 ||
    !same(view.kinds, d.kinds) ||
    view.duplicates !== d.duplicates ||
    view.range.field !== d.range.field ||
    view.range.from !== d.range.from ||
    view.range.to !== d.range.to ||
    Object.values(view.columns).some((v) => v.length > 0) ||
    view.tab !== d.tab ||
    view.search.trim() !== ""
  );
}

/**
 * Every filter the toolbar's own controls do not show as chosen, as a chip: what it says, and the
 * view without it. The warehouse picker and the tabs show theirs; a header filter shows nowhere else.
 */
export function viewChips(
  mode: Mode,
  view: GridView,
  today: string,
  headerOf: (column: string) => string,
  /** How a column's kept values read, when not as kept (the Division filter: "AXNY · FA, H"). */
  valuesText: (column: string, values: readonly string[]) => string = (_c, values) => values.join(", "),
): { id: string; text: string; without: GridView }[] {
  const d = defaultView(mode, today);
  const chips: { id: string; text: string; without: GridView }[] = [];
  if (view.customers.length)
    chips.push({ id: "customer", text: `Customer: ${view.customers.join(", ")}`, without: { ...view, customers: [] } });
  if (view.statuses.length)
    chips.push({
      id: "routing_status",
      text: `Routing status: ${view.statuses.join(", ")}`,
      without: { ...view, statuses: [] },
    });
  if (view.search.trim())
    chips.push({ id: "search", text: `Search: ${view.search.trim()}`, without: { ...view, search: "" } });
  const r = view.range;
  if (r.field !== d.range.field || r.from !== d.range.from || r.to !== d.range.to) {
    const field = [...WORKLIST_DATE_FIELDS, ...HISTORY_DATE_FIELDS].find((f) => f.id === r.field)?.label ?? r.field;
    const preset = (mode === "worklist" ? WORKLIST_PRESETS : HISTORY_PRESETS).find((p) => p.id === r.preset);
    const when =
      preset?.label ??
      (r.from && r.to
        ? `${formatDay(r.from)}–${formatDay(r.to)}`
        : r.from
          ? `from ${formatDay(r.from)}`
          : r.to
            ? `until ${formatDay(r.to)}`
            : "Any date");
    chips.push({ id: "date", text: `${field}: ${when}`, without: { ...view, range: d.range } });
  }
  for (const [id, values] of Object.entries(view.columns)) {
    if (!values.length) continue;
    const columns = { ...view.columns };
    delete columns[id];
    chips.push({
      id: `column:${id}`,
      text: `${headerOf(id)}: ${valuesText(id, values)}`,
      without: { ...view, columns },
    });
  }
  return chips;
}

/** The grid's state in the report's terms. `d=0`: the report's own defaults never add to it. */
export function toSearch(mode: Mode, v: GridView): Search {
  const s: Search = { d: "0" };
  const set = (column: string, values: readonly string[]) => {
    if (values.length) s[filterValueParam(column)] = [...values];
  };
  if (v.source !== "all") set("source", [v.source]);
  set("warehouse_group", v.warehouses);
  set("customer", v.customers);
  set("routing_status", v.statuses);
  if (mode === "history") {
    set("kind", v.kinds);
    if (!v.duplicates) set("duplicate", ["No"]);
    if (v.tab === "shipped") set("finished", ["Shipped"]);
    if (v.tab === "cancelled") set("finished", ["Cancelled"]);
  } else if (v.tab === "review") set("review", NEEDS_REVIEW);
  else if (STATE_TAB_LABEL[v.tab]) set("state_tab", [STATE_TAB_LABEL[v.tab]!]);
  for (const [id, values] of Object.entries(v.columns)) {
    // A division the header kept by its code is said by its name (`divisionFilterValue`).
    const said = id === "division" ? [...new Set(values.map(divisionFilterName))] : values;
    if (HEADER_COLUMNS[id] && !(id === "review" && v.tab === "review")) set(HEADER_COLUMNS[id], said);
  }
  // The Mixed tab is the report's mixed = Yes.
  if (mode === "worklist" && v.tab === "mixed") set("mixed", ["Yes"]);
  // The 2 values tab is two_values = Yes.
  if (mode === "worklist" && v.tab === "twovalues") set("two_values", ["Yes"]);
  if (mode === "worklist" && v.tab === "a2000") set("shipped_in_a2000", A2000_TAB);
  if (mode === "worklist" && v.tab === "dshipped") set("shipped_in_deposco", DEPOSCO_TAB);
  const date = DATE_COLUMNS[mode][v.range.field];
  if (date && (v.range.from || v.range.to)) {
    s[filterOperatorParam(date)] = "between";
    if (v.range.from) s[filterFromParam(date)] = v.range.from;
    if (v.range.to) s[filterToParam(date)] = v.range.to;
  }
  const sorted = v.sorting[0] && SORT_COLUMNS[v.sorting[0].id];
  if (sorted) {
    s.sort = sorted;
    s.dir = v.sorting[0]!.desc ? "desc" : "asc";
  }
  return s;
}

/**
 * A view the AI applied, merged into the grid's: what it names changes, `clear` empties those
 * parts, and the rest of the current view stays. Also the words for what the grid cannot show.
 */
export function fromSearch(
  mode: Mode,
  s: Search,
  current: GridView,
  labelOf: (column: string) => string,
  defaultSort?: { column: string; direction: string },
  clear: readonly string[] = [],
): { view: GridView; unapplied: string[] } {
  const used = new Set(["d", "sort", "dir", "offset", "limit"]);
  const unapplied: string[] = [];
  const take = (column: string) => {
    used.add(filterValueParam(column));
    return list(s[filterValueParam(column)]);
  };
  const cleared = partsOf(clear);
  const view: GridView = {
    ...current,
    source: cleared.has("source") ? "all" : current.source,
    warehouses: cleared.has("warehouse") ? [] : current.warehouses,
    customers: cleared.has("customer") ? [] : current.customers,
    statuses: cleared.has("routing_status") ? [] : current.statuses,
    kinds: cleared.has("kind") ? [] : current.kinds,
    duplicates: cleared.has("duplicates") ? true : current.duplicates,
    range: cleared.has("date") ? { field: current.range.field, preset: "any", from: null, to: null } : current.range,
    columns: cleared.has("columns") ? {} : { ...current.columns },
    tab: cleared.has("tab") ? "all" : current.tab,
    search: cleared.has("search") ? "" : current.search,
  };
  const replace = <T>(values: T[], set: (v: T[]) => void) => {
    if (values.length) set(values);
  };

  // One source, or both (back to All).
  const sources = take("source").filter((v): v is LineSource => (LINE_SOURCES as readonly string[]).includes(v));
  if (sources.length) view.source = sources.length === 1 ? sources[0]! : "all";
  const groups = take("warehouse_group");
  const raw = take("warehouse");
  const named = raw.filter((w): w is WarehouseGroup => (WAREHOUSE_GROUPS as readonly string[]).includes(w));
  replace(
    [...new Set([...groups, ...named])].filter((w): w is WarehouseGroup =>
      (WAREHOUSE_GROUPS as readonly string[]).includes(w),
    ),
    (w) => (view.warehouses = w),
  );
  if (named.length < raw.length)
    unapplied.push(`${labelOf("warehouse")}: ${raw.filter((w) => !named.includes(w as WarehouseGroup)).join(", ")}`);
  replace(take("customer"), (c) => (view.customers = c));
  replace(take("routing_status"), (c) => (view.statuses = c));
  if (mode === "history") {
    replace(take("kind"), (k) => (view.kinds = k));
    const duplicate = take("duplicate");
    if (duplicate.length) view.duplicates = !duplicate.includes("No");
  }

  // The tab: a term that picks one replaces the tab, and the header filters that also pick one.
  if (TAB_COLUMNS.some((c) => list(s[filterValueParam(c)]).length)) {
    view.tab = "all";
    for (const id of TAB_HEADERS) delete view.columns[id];
  }
  if (mode === "worklist") {
    const states = take("state_tab");
    const stateTab = Object.entries(STATE_TAB_LABEL).find(([, label]) => states.length === 1 && states[0] === label);
    const stateLabels = Object.values(STATE_TAB_LABEL);
    if (stateTab) view.tab = stateTab[0] as TabId;
    // Two tabs at once ("route today": Past deadline + Route now): the hidden Tab column's filter.
    else if (states.length > 1 && states.every((st) => stateLabels.includes(st))) view.columns.stateTab = states;
    else if (states.length) unapplied.push(`${labelOf("state_tab")}: ${states.join(", ")}`);
    // A window state is a tab when its states are exactly the tab's: Past deadline, or Route today
    // with Route by tomorrow (Route now).
    const windows = take("window_state");
    const byWindow = WINDOW_TABS.find(
      (t) => windows.length === t.states.length && t.states.every((w) => windows.includes(w)),
    );
    if (byWindow && view.tab === "all") view.tab = byWindow.tab;
    else if (windows.length) unapplied.push(`${labelOf("window_state")}: ${windows.join(", ")}`);
    const review = take("review");
    if (
      review.length &&
      view.tab === "all" &&
      NEEDS_REVIEW.every((r) => review.includes(r)) &&
      review.length === NEEDS_REVIEW.length
    ) {
      view.tab = "review";
    } else if (review.length === 1 && review[0] === REVIEW_LABELS.nowms && view.tab === "all") view.tab = "nowms";
    else if (review.length) view.columns.review = review;
    // No WMS rows: their tab.
    const inWms = take("in_wms");
    if (inWms.length === 1 && inWms[0] === "No" && view.tab === "all") view.tab = "nowms";
    else if (inWms.length) unapplied.push(`${labelOf("in_wms")}: ${inWms.join(", ")}`);
    // Mixed lines: the Mixed tab, unless another tab is already taken.
    const mixed = take("mixed");
    if (mixed.length === 1 && mixed[0] === "Yes" && view.tab === "all") view.tab = "mixed";
    else if (mixed.length) view.columns.mixed = mixed;
    // Lines with a "2 values" choice open: their tab; the grid has no column for the rest.
    const twoValues = take("two_values");
    if (twoValues.length === 1 && twoValues[0] === "Yes" && view.tab === "all") view.tab = "twovalues";
    else if (twoValues.length) unapplied.push(`${labelOf("two_values")}: ${twoValues.join(", ")}`);
    // Shipped in A2000 (all or some): its tab, unless another tab is already taken.
    const a2000 = take("shipped_in_a2000");
    if (a2000.length === A2000_TAB.length && A2000_TAB.every((v) => a2000.includes(v)) && view.tab === "all")
      view.tab = "a2000";
    else if (a2000.length) view.columns.a2000 = a2000;
    // Shipped in Deposco, likewise.
    const dshipped = take("shipped_in_deposco");
    if (dshipped.length === DEPOSCO_TAB.length && DEPOSCO_TAB.every((v) => dshipped.includes(v)) && view.tab === "all")
      view.tab = "dshipped";
    else if (dshipped.length) view.columns.dshipped = dshipped;
  } else {
    const finished = take("finished");
    const tab = HISTORY_TABS.find((t) => finished.length === 1 && t.label === finished[0]);
    if (tab) view.tab = tab.id;
    else if (finished.length > 1) unapplied.push(`${labelOf("finished")}: ${finished.join(", ")}`);
  }

  for (const [id, column] of Object.entries(HEADER_COLUMNS)) {
    if (id === "review" || (mode === "worklist" && TAB_HEADERS.includes(id))) continue;
    replace(take(column), (values) => (view.columns[id] = values));
  }

  // One date range: the grid has one. A date the AI gives replaces the grid's.
  const dates = swap(DATE_COLUMNS[mode] as Record<DateFieldId, string>);
  let dated = false;
  for (const [column, field] of Object.entries(dates)) {
    const op = one(s[filterOperatorParam(column)]);
    const value = one(s[filterValueParam(column)]);
    const from = one(s[filterFromParam(column)]) ?? (op === "after" ? value : undefined);
    const to = one(s[filterToParam(column)]) ?? (op === "before" ? value : undefined);
    for (const k of [
      filterOperatorParam(column),
      filterValueParam(column),
      filterFromParam(column),
      filterToParam(column),
    ])
      used.add(k);
    if (!from && !to) continue;
    if (dated) {
      unapplied.push(`${labelOf(column)} ${from ?? "…"} – ${to ?? "…"} (one date range at a time)`);
      continue;
    }
    dated = true;
    // Days a preset of the picker names ("Last 2 weeks", "Since Jan 1, 2026") come back as that preset.
    const preset = (mode === "worklist" ? WORKLIST_PRESETS : HISTORY_PRESETS).find((p) => {
      const r = presetRange(p.id, localToday());
      return r.from === (from ?? null) && r.to === (to ?? null);
    });
    view.range = { field, preset: preset?.id ?? "custom", from: from ?? null, to: to ?? null };
  }

  // "Pick ticket 1809981", a PO, a WMS or routing reference: the search box.
  const finds = SEARCH_COLUMNS.flatMap((c) => take(c));
  if (finds.length === 1) view.search = finds[0]!;
  else if (finds.length > 1) unapplied.push(`search for ${finds.join(" and ")} (one search at a time)`);

  // The report's default sort is the grid's own order (most urgent first; History last finished).
  const sortBy = one(s.sort);
  const isDefault =
    defaultSort !== undefined &&
    sortBy === defaultSort.column &&
    (one(s.dir) ?? defaultSort.direction) === defaultSort.direction;
  if (isDefault) view.sorting = [];
  else if (sortBy) {
    const id = Object.entries(SORT_COLUMNS).find(([, c]) => c === sortBy)?.[0];
    if (id) view.sorting = [{ id, desc: one(s.dir) === "desc" }];
    else unapplied.push(`sort by ${labelOf(sortBy)}`);
  }

  // Everything else the AI set: a number filter, a grouping, a summary, a column this grid lacks.
  const rest = new Map<string, string>();
  for (const key of Object.keys(s)) {
    if (used.has(key)) continue;
    const column = key.startsWith("f_") ? key.slice(2).replace(/_(op|from|to)$/, "") : null;
    rest.set(column ?? key, column ? labelOf(column) : key === "g" ? "grouping" : "summary");
  }
  unapplied.push(...new Set(rest.values()));
  return { view, unapplied };
}

/** A tool's query as the report's terms, as `apply_view` gives them to `fromSearch`. */
export function termsOf(q: AskQuery & { group_by?: string }): Search {
  const s: Search = { d: "0" };
  for (const [column, values] of Object.entries(q.filters ?? {}))
    if (values.length) s[filterValueParam(column)] = [...values];
  for (const d of q.dates ?? []) {
    s[filterOperatorParam(d.column)] = "between";
    if (d.from) s[filterFromParam(d.column)] = d.from;
    if (d.to) s[filterToParam(d.column)] = d.to;
  }
  for (const n of q.numbers ?? []) {
    s[filterOperatorParam(n.column)] = "between";
    if (n.min !== undefined) s[filterFromParam(n.column)] = String(n.min);
    if (n.max !== undefined) s[filterToParam(n.column)] = String(n.max);
  }
  if (q.group_by) s.g = q.group_by;
  if (q.search) s[filterValueParam("pick_tickets")] = q.search;
  if (q.sort) {
    s.sort = q.sort;
    s.dir = q.desc ? "desc" : "asc";
  }
  return s;
}

/** What `apply_view` clears for `all_history`: History's own filters (orders, no duplicates, since 2026). */
export const ALL_HISTORY_CLEARS: ViewPart[] = ["kind", "duplicates", "date"];

/** A count's scope: the view's filters it keeps, the tab (null: every line), and what it left out. */
export interface Scope {
  view: GridView;
  tab: TabId | null;
  /** The view's parts this query does not keep: it names them itself, or the question said so. */
  dropped: ViewPart[];
}

/**
 * The grid's view as a query's default scope (`query_worklist`, `query_history`, `order_value`): every
 * part of it, except what the query names itself (a warehouse, a customer, a tab, the same date)
 * and what the question leaves out (`ignore`: "in all warehouses" → warehouse, "any date" → date).
 */
export function scopeOf(mode: Mode, view: GridView, q: AskQuery, ignore: readonly string[] = []): Scope {
  const dropped = partsOf(ignore);
  const f = q.filters ?? {};
  const has = (column: string) => (f[column]?.length ?? 0) > 0;
  if (has("source")) dropped.add("source");
  if (has("warehouse") || has("warehouse_group")) dropped.add("warehouse");
  if (has("customer")) dropped.add("customer");
  if (has("routing_status")) dropped.add("routing_status");
  if (q.search || SEARCH_COLUMNS.some(has)) dropped.add("search");
  if (TAB_COLUMNS.some(has)) dropped.add("tab");
  if (has("kind")) dropped.add("kind");
  if (has("duplicate")) dropped.add("duplicates");
  if (q.all_history) for (const p of ALL_HISTORY_CLEARS) dropped.add(p);
  const dateColumn = DATE_COLUMNS[mode][view.range.field];
  if ((q.dates ?? []).some((d) => d.column === dateColumn)) dropped.add("date");
  const columns = dropped.has("columns")
    ? {}
    : Object.fromEntries(
        Object.entries(view.columns).filter(
          ([id, values]) =>
            values.length > 0 && !has(HEADER_COLUMNS[id] ?? id) && !(dropped.has("tab") && TAB_HEADERS.includes(id)),
        ),
      );
  const viewed = {
    source: view.source !== "all",
    warehouse: view.warehouses.length > 0,
    date: !!(view.range.from || view.range.to),
    customer: view.customers.length > 0,
    routing_status: view.statuses.length > 0,
    search: view.search.trim() !== "",
    tab: view.tab !== "all",
    columns: Object.keys(view.columns).some((id) => (view.columns[id]?.length ?? 0) > 0 && !(id in columns)),
    kind: view.kinds.length > 0,
    duplicates: !view.duplicates,
  };
  return {
    view: {
      ...view,
      source: dropped.has("source") ? "all" : view.source,
      warehouses: dropped.has("warehouse") ? [] : view.warehouses,
      customers: dropped.has("customer") ? [] : view.customers,
      statuses: dropped.has("routing_status") ? [] : view.statuses,
      kinds: dropped.has("kind") ? [] : view.kinds,
      duplicates: dropped.has("duplicates") ? true : view.duplicates,
      range: dropped.has("date") ? { ...view.range, preset: "any", from: null, to: null } : view.range,
      search: dropped.has("search") ? "" : view.search,
      columns,
    },
    tab: dropped.has("tab") ? null : view.tab,
    // Only what the view had: leaving out a filter it did not have changes nothing.
    dropped: VIEW_PARTS.filter((p) => dropped.has(p) && viewed[p]),
  };
}

/** The lines a scope keeps, as the grid counts them: the ids the lines reports name them by. */
export function scopeLineIds(rows: readonly Row[], mode: Mode, scope: Scope, columnValue: ColumnValue): string[] {
  return pageRows(rows, mode, scope.view, columnValue)
    .filter((r) => scope.tab === null || inTab(r, scope.tab))
    .map((r) => lineIdOf(r.line));
}

/** A line's id in the lines reports (`line_id`): its record's, else its key. */
export const lineIdOf = (l: Pick<LineView, "key" | "stored">) => l.stored?.id ?? l.key;

const DATE_WORDS: Partial<Record<DateFieldId, string>> = {
  ptDate: "received",
  start: "first ship",
  cancel: "cancel",
  routeBy: "route by",
  readyDate: "ready",
  schedShip: "scheduled ship",
  appointmentDate: "appointment",
  routedOn: "routed",
  finishedAt: "finished",
};

/** "received in the last 2 weeks", "cancel 10/20–10/26": a view's date range in words. */
function rangeWords(mode: Mode, r: DateRange): string {
  const word =
    DATE_WORDS[r.field] ??
    [...WORKLIST_DATE_FIELDS, ...HISTORY_DATE_FIELDS].find((f) => f.id === r.field)?.label.toLowerCase() ??
    r.field;
  const preset = (mode === "worklist" ? WORKLIST_PRESETS : HISTORY_PRESETS).find((p) => p.id === r.preset);
  if (preset) {
    const label = preset.label.charAt(0).toLowerCase() + preset.label.slice(1);
    return `${word} ${/^(last|next) /.test(label) ? `in the ${label}` : label}`;
  }
  if (r.from && r.to) return `${word} ${formatDay(r.from)}–${formatDay(r.to)}`;
  return r.from ? `${word} from ${formatDay(r.from)}` : `${word} until ${formatDay(r.to!)}`;
}

/** A scope in words, as an answer says it: "PE + HT, received in the last 2 weeks". */
/**
 * A read of `keep` out of `every` line id: `within` or `outside`, whichever is shorter (a request
 * body stays small); nothing when it keeps them all. My customers makes `every` larger than the
 * page's lines in view: its counts, and "without the view", stay within them.
 */
export function limitTo(keep: readonly string[], every: readonly string[]): { within?: string[]; outside?: string[] } {
  const kept = new Set(keep);
  const outside = every.filter((id) => !kept.has(id));
  if (!outside.length) return {};
  return keep.length <= outside.length ? { within: [...keep] } : { outside };
}

export function scopeWords(mode: Mode, scope: Scope, headerOf: (column: string) => string = (c) => c): string {
  const v = scope.view;
  const tabs = mode === "worklist" ? WORKLIST_TABS : HISTORY_TABS;
  const parts = [
    v.source !== "all" ? `${v.source} only` : "",
    v.warehouses.length ? v.warehouses.join(" + ") : "all warehouses",
    v.range.from || v.range.to ? rangeWords(mode, v.range) : "",
    v.customers.length ? `customer ${v.customers.join(" or ")}` : "",
    v.statuses.length ? `routing status ${v.statuses.join(" or ")}` : "",
    v.search.trim() ? `search "${v.search.trim()}"` : "",
    scope.tab && scope.tab !== "all" ? `tab ${tabs.find((t) => t.id === scope.tab)?.label ?? scope.tab}` : "",
    ...Object.entries(v.columns)
      .filter(([, values]) => values.length)
      .map(([id, values]) => `${headerOf(id)} ${values.join(" or ")}`),
  ];
  return parts.filter(Boolean).join(", ");
}

/** What a count without the view leaves out, as "across all …" says it: "warehouses and dates". */
export function withoutViewWords(mode: Mode, view: GridView): string {
  const d = defaultView(mode, localToday());
  const nouns = [
    view.source !== d.source ? "sources" : "",
    view.warehouses.length ? "warehouses" : "",
    view.range.from || view.range.to ? "dates" : "",
    view.customers.length ? "customers" : "",
    view.statuses.length ? "routing statuses" : "",
    view.search.trim() ? "searches" : "",
    view.tab !== d.tab ? "tabs" : "",
    Object.values(view.columns).some((v) => v.length) ? "column filters" : "",
  ].filter(Boolean);
  return nouns.length > 1 ? `${nouns.slice(0, -1).join(", ")} and ${nouns.at(-1)}` : (nouns[0] ?? "");
}

/** Whether a count in the view and the same count without it differ enough to say both. */
export const differsALot = (inView: number, everywhere: number) =>
  everywhere - inView >= Math.max(5, Math.ceil(everywhere * 0.1));

/** A view Ask AI set, while it may be undone: the view before it, and the one it set as a key. */
export interface ViewStep<V> {
  before: V;
  /** The view it set, as the page's version key: Undo shows while the grid is still that view. */
  version: string;
  unapplied: string[];
}

/**
 * Ask AI's steps after one more, last on top. A bar question (`question`: the view when it was
 * asked) that sets the grid twice is one step, back to before the question.
 */
export const pushStep = <V>(steps: readonly ViewStep<V>[], step: ViewStep<V>, question: V | null): ViewStep<V>[] => [
  ...(question !== null && steps.at(-1)?.before === question ? steps.slice(0, -1) : steps),
  step,
];

/** The step Undo takes back now: the last one, while the grid is still the view it set. */
export const undoableStep = <V>(steps: readonly ViewStep<V>[], version: string): ViewStep<V> | null => {
  const top = steps.at(-1);
  return top && top.version === version ? top : null;
};
